import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertTriangle, CreditCard, Clock, Download, LogOut, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentRequiredScreen({ user, paymentStatus, onLogout, onPaymentComplete }) {
  const [loading, setLoading] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);

  useEffect(() => {
    // Try to get subscription amount from platform settings
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/platform/settings`);
        setPlatformSettings(res.data);
      } catch (error) {
        // Non-admin users won't have access, use default
        setPlatformSettings({ monthly_subscription_amount: 4999 });
      }
    };
    fetchSettings();
  }, []);

  const handlePayWithSquare = async () => {
    setLoading(true);
    try {
      // This would integrate with Square checkout
      // For now, show a message to contact support
      toast.info('Square payment integration coming soon. Please contact support to make a payment.');
      
      // In the future, this would:
      // 1. Create a Square checkout session
      // 2. Redirect to Square hosted checkout
      // 3. Handle webhook for payment completion
      
    } catch (error) {
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async (format) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/account/export-data?format=${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `export.${format}`;
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const subscriptionAmount = platformSettings?.monthly_subscription_amount 
    ? (platformSettings.monthly_subscription_amount / 100).toFixed(2)
    : '49.99';

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-4" data-testid="payment-required-screen">
      <Card className="w-full max-w-lg bg-card border-border">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <CardTitle className="text-2xl">Payment Required</CardTitle>
          <CardDescription className="text-muted-foreground">
            {paymentStatus?.reason === 'trial_expired' 
              ? 'Your free trial has ended'
              : 'Your subscription payment is overdue'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Status Info */}
          <div className="bg-muted/20 rounded-lg p-4 space-y-2">
            {paymentStatus?.reason === 'trial_expired' ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Trial ended:</span>
                  <span>{paymentStatus?.trial_ended ? new Date(paymentStatus.trial_ended).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Grace period ended:</span>
                  <span>{paymentStatus?.grace_period_ended ? new Date(paymentStatus.grace_period_ended).toLocaleDateString() : 'N/A'}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Payment was due:</span>
                  <span>{paymentStatus?.overdue_since ? new Date(paymentStatus.overdue_since).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Days overdue:</span>
                  <span className="text-red-400 font-medium">{paymentStatus?.days_overdue || 0} days</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground">Monthly subscription:</span>
              <span className="text-lg font-bold text-neon-blue">${subscriptionAmount}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={handlePayWithSquare}
              disabled={loading}
              className="w-full bg-neon-blue text-black hover:bg-neon-blue/80 h-12"
              data-testid="pay-now-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Pay Now with Square
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              Your account will be reactivated immediately after payment
            </p>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          {/* Export Data Section */}
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Need to export your data before deciding?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleExportData('json')}
                disabled={loading}
                className="flex-1 border-border"
                data-testid="export-json-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExportData('zip')}
                disabled={loading}
                className="flex-1 border-border"
                data-testid="export-zip-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export ZIP
              </Button>
            </div>
          </div>

          {/* Logout */}
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full text-muted-foreground hover:text-foreground"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>

          {/* Contact Support */}
          <p className="text-xs text-center text-muted-foreground">
            Having issues? Contact support at support@djcrm.com
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
